import { View, Text, styled, ScrollView } from 'tamagui'
import { useRef, useEffect, useState, useCallback } from 'react'
import { ArrowDown } from '@tamagui/lucide-icons'
import { messages, users, lastReadMessageId } from '@/mocks'
import { MessageItem } from './MessageItem'

const ListContainer = styled(View, {
  flex: 1,
  minHeight: 0,
  position: 'relative',
})

const WelcomeSection = styled(View, {
  paddingHorizontal: 16,
  paddingTop: 48,
  paddingBottom: 16,
})

const WelcomeTitle = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '700',
  fontSize: 28,
  color: '$surfaceForeground',
  marginBottom: 8,
})

const WelcomeSubtitle = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 14,
  color: '$sidebarMuted',
  marginBottom: 16,
})

const WelcomeDivider = styled(View, {
  height: 1,
  backgroundColor: '$borderDefault',
})

const MessagePadding = styled(View, {
  paddingHorizontal: 16,
  paddingBottom: 8,
})

// NEW unread divider styles
const NewDividerContainer = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  paddingVertical: 6,
  paddingHorizontal: 4,
  gap: 8,
})

const NewDividerLine = styled(View, {
  flex: 1,
  height: 1,
  backgroundColor: '$error',
})

const NewDividerLabel = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 11,
  fontWeight: '700',
  color: '$error',
  textTransform: 'uppercase',
})

// Day separator styles
const DaySeparatorContainer = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  paddingVertical: 8,
  paddingHorizontal: 4,
  gap: 12,
})

const DaySeparatorLine = styled(View, {
  flex: 1,
  height: 1,
  backgroundColor: '$borderDefault',
})

const DaySeparatorLabel = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 12,
  fontWeight: '600',
  color: '$color8',
  // @ts-expect-error -- web white-space
  style: { whiteSpace: 'nowrap' },
})

// Jump to bottom button
const JumpToBottomButton = styled(View, {
  position: 'absolute' as any,
  bottom: 16,
  right: 24,
  width: 40,
  height: 40,
  borderRadius: 20,
  backgroundColor: '$surfaceRaised',
  borderWidth: 1,
  borderColor: '$borderDefault',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  zIndex: 100,
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition + shadow
  style: {
    transition: 'background-color 150ms ease, opacity 200ms ease',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  },
})

function formatDayLabel(timestamp: number): string {
  const date = new Date(timestamp)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today.getTime() - 86_400_000)
  const messageDay = new Date(date.getFullYear(), date.getMonth(), date.getDate())

  if (messageDay.getTime() === today.getTime()) return 'Today'
  if (messageDay.getTime() === yesterday.getTime()) return 'Yesterday'

  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

function getDayKey(timestamp: number): string {
  const date = new Date(timestamp)
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
}

interface MessageListProps {
  channelId: string
}

export function MessageList({ channelId }: MessageListProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showJumpToBottom, setShowJumpToBottom] = useState(false)
  const channelMessages = messages[channelId] || []

  // Scroll to bottom on mount and channel change
  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current as unknown as HTMLElement
      el.scrollTop = el.scrollHeight
    }
  }, [channelId])

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return
    const el = scrollRef.current as unknown as HTMLElement
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    setShowJumpToBottom(distFromBottom > 200)
  }, [])

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      const el = scrollRef.current as unknown as HTMLElement
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    }
  }, [])

  // Build message groups with day separators and unread divider
  const lastReadId = lastReadMessageId[channelId]
  const renderMessages = () => {
    const elements: React.ReactNode[] = []
    let lastDayKey: string | null = null

    for (let index = 0; index < channelMessages.length; index++) {
      const msg = channelMessages[index]
      const currentDayKey = getDayKey(msg.timestamp)

      // Insert day separator if day changed
      if (currentDayKey !== lastDayKey) {
        elements.push(
          <DaySeparatorContainer key={`day-${currentDayKey}`}>
            <DaySeparatorLine />
            <DaySeparatorLabel>{formatDayLabel(msg.timestamp)}</DaySeparatorLabel>
            <DaySeparatorLine />
          </DaySeparatorContainer>
        )
        lastDayKey = currentDayKey
      }

      const prevMsg = index > 0 ? channelMessages[index - 1] : null
      const prevDayKey = prevMsg ? getDayKey(prevMsg.timestamp) : null
      const isSameDay = currentDayKey === prevDayKey
      const isGrouped = isSameDay
        && prevMsg
        && prevMsg.authorId === msg.authorId
        && (msg.timestamp - prevMsg.timestamp) < 5 * 60_000

      elements.push(
        <MessageItem
          key={msg.id}
          message={msg}
          user={users[msg.authorId]}
          showAvatar={!isGrouped}
        />
      )

      // Insert NEW unread divider after the last read message
      if (lastReadId && msg.id === lastReadId && index < channelMessages.length - 1) {
        elements.push(
          <NewDividerContainer key="new-divider">
            <NewDividerLine />
            <NewDividerLabel>NEW</NewDividerLabel>
            <NewDividerLine />
          </NewDividerContainer>
        )
      }
    }

    return elements
  }

  return (
    <ListContainer>
      <ScrollView
        flex={1}
        ref={scrollRef as any}
        showsVerticalScrollIndicator={false}
        onScroll={handleScroll}
        scrollEventThrottle={100}
      >
        <WelcomeSection>
          <WelcomeTitle># {channelId === 'ch3' ? 'general' : 'channel'}</WelcomeTitle>
          <WelcomeSubtitle>
            This is the start of the channel. Say something nice.
          </WelcomeSubtitle>
          <WelcomeDivider />
        </WelcomeSection>

        <MessagePadding>
          {renderMessages()}
        </MessagePadding>
      </ScrollView>

      {showJumpToBottom && (
        <JumpToBottomButton onPress={scrollToBottom}>
          <ArrowDown size={18} color="$accent" />
        </JumpToBottomButton>
      )}
    </ListContainer>
  )
}
